// qa-app/src/ai-coding-outputs/evidenceUtils.ts
import type { EvidenceHighlight } from "@/components/ui/document-viewer";
import type { DiagnosisAction, DiagnosisEvidenceRow } from "./human-coding/types";
import type { UnmatchedLeadTerm } from "./human-coding/useUnmatchedLeadTerms";

export const UNMATCHED_PREFIX = "unmatched::";

export function countTimelineQuotes(timeline: DiagnosisAction[]): number {
  return timeline.reduce((sum, a) => sum + (a.evidenceQuotes?.length ?? 0), 0);
}

export function uniqueQuoteTexts(timeline: DiagnosisAction[]): string[] {
  const texts = timeline.flatMap((a) => (a.evidenceQuotes ?? []).map((q) => q.text.trim()));
  return [...new Set(texts.filter((t) => t.length > 0))];
}

export function findQuoteIndex(timeline: DiagnosisAction[], quote: { text: string; fileIdentifier: string }): number {
  let index = 0;
  for (const action of timeline) {
    if (!action.evidenceQuotes) continue;
    for (const eq of action.evidenceQuotes) {
      if (eq.text === quote.text && eq.fileIdentifier === quote.fileIdentifier) return index;
      index++;
    }
  }
  return -1;
}

export function buildAllHighlights(
  evidenceData: DiagnosisEvidenceRow[],
  focusedCode: string | null,
  focusedQuoteIndex: number,
  unmatchedLeadTerms?: UnmatchedLeadTerm[],
): EvidenceHighlight[] {
  const highlights: EvidenceHighlight[] = [];
  for (const row of evidenceData) {
    const isFocused = row.icd10Code === focusedCode;
    let quoteIndexInCode = 0;
    for (const action of row.timeline) {
      if (!action.evidenceQuotes || action.evidenceQuotes.length === 0) continue;
      for (const quote of action.evidenceQuotes) {
        highlights.push({
          text: quote.text,
          code: row.icd10Code,
          reason: action.reason,
          isFocused,
          isActiveQuote: isFocused && quoteIndexInCode === focusedQuoteIndex,
          fileIdentifier: quote.fileIdentifier,
        });
        quoteIndexInCode++;
      }
    }
  }

  if (unmatchedLeadTerms) {
    for (const term of unmatchedLeadTerms) {
      const syntheticCode = `${UNMATCHED_PREFIX}${term.leadTerm}`;
      const isFocused = syntheticCode === focusedCode;
      let quoteIndex = 0;
      for (const quote of term.evidenceQuotes) {
        highlights.push({
          text: quote.text,
          code: syntheticCode,
          isFocused,
          isActiveQuote: isFocused && quoteIndex === focusedQuoteIndex,
          fileIdentifier: quote.fileIdentifier,
          highlightVariant: "unmatched",
        });
        quoteIndex++;
      }
    }
  }

  return highlights;
}
